import { FC, useState } from 'react';
import { useTranslation } from "react-i18next";
import { Box, Button, Menu, MenuItem, styled, Radio } from '@mui/material';
import { Project } from '../models/project';

interface SelectProjectProps {
    projects: Project[];
    selectedProject?: Project;
    onProjectSelect: (project: Project) => void;
    placeholder?: string;
    disabled?: boolean;
    isLoading?: boolean;
    variant?: 'text' | 'outlined' | 'contained';
    size?: 'small' | 'medium' | 'large';
    sx?: {
        [key: string]: string | number | any
    }
}

const StyledButton = styled(Button)(({ theme }) => ({
    minWidth: '200px',
    justifyContent: 'center',
    textTransform: 'none',
    backgroundColor: 'rgba(0, 0, 0, 0.08)',
    padding: theme.spacing(0.6, 2),
}));

export const SelectProject: FC<SelectProjectProps> = ({
    projects,
    selectedProject,
    onProjectSelect,
    placeholder = 'meeting.project_label',
    disabled = false,
    isLoading = false,
    variant = 'outlined',
    size = 'medium',
    sx
}) => {
    const { t } = useTranslation();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleProjectSelect = (project: Project) => {
        onProjectSelect(project);
        handleClose();
    };

    return (
        <Box>
            <StyledButton
                onClick={handleClick}
                variant={variant}
                disabled={disabled || isLoading}
                size={size}
                sx={sx}
            >
                {selectedProject?.name || t(placeholder)}
            </StyledButton>
            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }} slotProps={{ paper: {
                    sx: {
                        maxHeight: 300,
                        minWidth: '200px',
                        backgroundColor: 'white'
                    }
                } }}
            >
                {projects?.map((project) => (
                    <MenuItem
                        key={project.id}
                        onClick={() => handleProjectSelect(project)}
                        selected={project.id === selectedProject?.id}
                        sx={theme => ({
                            '&:hover': {
                                backgroundColor: theme.palette.action.hover,
                            }
                        })}
                    >
                        <Radio
                            checked={project.id === selectedProject?.id}
                            sx={{ padding: '4px 8px 4px 4px' }}
                        />
                        {project.name}
                    </MenuItem>
                ))}
            </Menu>
        </Box>
    );
};